import { showToast } from './Toast.js';
import type { VerificationResult } from '../../core/types.js';

export function renderVerify(container: HTMLElement): void {
  container.innerHTML = `
    <div class="page-header">
      <h1 class="page-title">Verify Certificate</h1>
      <p class="page-subtitle">Validate the authenticity and integrity of any Preuve AI compliance certificate</p>
    </div>

    <div class="card" style="max-width:600px">
      <div class="form-row">
        <label>Certificate ID</label>
        <input type="text" id="cert-id-input" placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000" />
      </div>
      <div class="form-row" style="margin-bottom:0">
        <label>Or scan QR code</label>
        <div style="border:2px dashed var(--border);border-radius:var(--radius-md);padding:32px;text-align:center;color:var(--text-muted)">
          <div style="font-size:32px;margin-bottom:8px">⬡</div>
          <div style="font-size:13px">QR scan available in Electron desktop app</div>
          <div style="font-size:11px;margin-top:4px">or on mobile via Capacitor app</div>
        </div>
      </div>

      <div style="margin-top:18px">
        <button class="btn btn-gold btn-lg" id="verify-btn" style="width:100%">✓ Verify Certificate</button>
      </div>
    </div>

    <div id="verify-result" style="display:none;margin-top:24px;max-width:600px"></div>
  `;

  document.getElementById('verify-btn')!.addEventListener('click', async () => {
    const certId = (document.getElementById('cert-id-input') as HTMLInputElement).value.trim();
    if (!certId) {
      showToast('Please enter a Certificate ID', 'error');
      return;
    }

    const btn = document.getElementById('verify-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.innerHTML = '<span class="spinner"></span> Verifying…';

    try {
      const preuve = (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
      const result = await preuve.verifyCertificate(certId) as VerificationResult;

      renderResult(result);
    } catch (err) {
      renderResult(null, (err as Error).message);
    } finally {
      btn.disabled = false;
      btn.textContent = '✓ Verify Certificate';
    }
  });

  // Allow Enter key
  document.getElementById('cert-id-input')!.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') document.getElementById('verify-btn')!.click();
  });
}

function renderResult(result: VerificationResult | null, errorMsg?: string): void {
  const panel = document.getElementById('verify-result')!;
  panel.style.display = 'block';

  if (!result || errorMsg) {
    panel.innerHTML = `
      <div class="verify-result invalid">
        <div style="font-size:15px;font-weight:700;color:var(--danger);margin-bottom:8px">✕ Verification Failed</div>
        <div style="font-size:13px;color:var(--text-secondary)">${errorMsg ?? 'An unknown error occurred.'}</div>
      </div>
    `;
    return;
  }

  const statusColor = result.isValid ? 'var(--success)' : 'var(--danger)';
  const statusIcon = result.isValid ? '✓' : '✕';
  const statusClass = result.isValid ? 'valid' : 'invalid';

  panel.innerHTML = `
    <div class="verify-result ${statusClass}">
      <div style="font-size:18px;font-weight:800;color:${statusColor};margin-bottom:16px">
        ${statusIcon} Certificate ${result.isValid ? 'Valid' : 'Invalid'}
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px">
        <div>
          <div style="font-size:10px;color:var(--text-muted);font-weight:700;text-transform:uppercase;letter-spacing:1px">Certificate ID</div>
          <div style="font-size:12px;font-family:var(--font-mono);margin-top:4px;color:var(--text-primary)">${result.certId}</div>
        </div>
        <div>
          <div style="font-size:10px;color:var(--text-muted);font-weight:700;text-transform:uppercase;letter-spacing:1px">Status</div>
          <div style="margin-top:4px"><span class="badge badge-${result.status}">${result.status}</span></div>
        </div>
        <div>
          <div style="font-size:10px;color:var(--text-muted);font-weight:700;text-transform:uppercase;letter-spacing:1px">Document</div>
          <div style="font-size:13px;margin-top:4px;color:var(--text-primary)">${result.documentTitle || '—'}</div>
        </div>
        <div>
          <div style="font-size:10px;color:var(--text-muted);font-weight:700;text-transform:uppercase;letter-spacing:1px">Human Edit %</div>
          <div style="font-size:13px;margin-top:4px;color:var(--text-primary)">${result.humanEditPercentage}%</div>
        </div>
        <div>
          <div style="font-size:10px;color:var(--text-muted);font-weight:700;text-transform:uppercase;letter-spacing:1px">Issued At</div>
          <div style="font-size:12px;margin-top:4px;color:var(--text-secondary)">${result.issuedAt ? new Date(result.issuedAt).toLocaleString() : '—'}</div>
        </div>
        <div>
          <div style="font-size:10px;color:var(--text-muted);font-weight:700;text-transform:uppercase;letter-spacing:1px">Verified At</div>
          <div style="font-size:12px;margin-top:4px;color:var(--text-secondary)">${new Date(result.verifiedAt).toLocaleString()}</div>
        </div>
      </div>

      ${!result.isValid ? `
        <div style="background:rgba(239,68,68,.1);border-radius:6px;padding:10px 14px;font-size:12px;color:var(--danger)">
          ⚠ This certificate could not be verified. The document may have been tampered with, or the certificate has been revoked.
        </div>
      ` : `
        <div style="background:rgba(16,185,129,.08);border-radius:6px;padding:10px 14px;font-size:12px;color:var(--success)">
          ◈ Cryptographic signature verified. Audit chain integrity confirmed.
        </div>
      `}
    </div>
  `;
}
